import React from 'react';
import {StyleSheet, View, ViewStyle} from 'react-native';

import {useWebScreenDimensions} from '../hooks/useWebScreenDimensions';
import {Shop} from '../models/middleware';
import theme from '../style/theme';
import {renderAddressLines} from '../utils/address';
import {
  hasGiftCardDeliveryCapability,
  hasGiftCardPickupCapability,
  hasInStoreGiftCardChannel,
  hasOnlineGiftCardChannel,
} from '../utils/giftCards';
import {formatOpenTodayText} from '../utils/storeHours';

import Text from './themed/Text';
import GiftCardStoreCapabilityChips, {
  CAPABILITY_CHIPS_COL_WIDTH,
} from './GiftCardStoreCapabilityChips';
import {Icon} from './Icon';

const PRIMARY = theme.colour.text.heading?.primary ?? '#0054A6';

// Fixed column widths so distance + capability chips align across rows
// like a true table column on desktop. Tweak in one place.
export const DISTANCE_COL_WIDTH = 64;
export {CAPABILITY_CHIPS_COL_WIDTH};

// DIS-1501: hide the "Online Shopping Options" (Pick up / Delivery capability
// chips) column pending a trustworthy delivery/pickup data source. Code stays
// in place — flip this to `true` to re-enable the chips across the desktop
// table column + mobile cards + the desktop column header. Typed `boolean` so
// the toggle reads as reachable to the linter/type-checker.
export const SHOW_ONLINE_SHOPPING_OPTIONS: boolean = false;

// Below this width the body-column on mobile gets too narrow to hold the
// pills row legibly (pills clip against the available width). At/under
// this breakpoint the bottom section escapes the body column and spans
// the full card width instead.
const NARROW_MOBILE_BREAKPOINT = 480;

type PillVariant = 'instore' | 'online';

const PILL_VARIANTS: Record<
  PillVariant,
  {label: string; bg: string; border: string; text: string; icon: string}
> = {
  instore: {
    label: 'Instore redemption',
    bg: '#FEF3C7',
    border: '#FCD34D',
    text: '#B45309',
    icon: 'gift',
  },
  online: {
    label: 'Online redemption',
    bg: '#DBEAFE',
    border: '#93C5FD',
    text: '#1D4ED8',
    icon: 'envelope',
  },
};

const Pill = ({variant}: {variant: PillVariant}) => {
  const v = PILL_VARIANTS[variant];
  return (
    <View
      style={[styles.pill, {backgroundColor: v.bg, borderColor: v.border}]}
      testID={`gift-card-store-pill-${variant}`}>
      <Icon name={v.icon} size={10} color={v.text} />
      <Text style={[styles.pillText, {color: v.text}]}>{v.label}</Text>
    </View>
  );
};

export type GiftCardStoreCardProps = {
  item: Shop;
  style?: ViewStyle;
  testID?: string;
};

const GiftCardStoreCard = ({item, style, testID}: GiftCardStoreCardProps) => {
  const {isWebDesktopScreen, screenWidth} = useWebScreenDimensions();
  const isNarrowMobile =
    !isWebDesktopScreen && screenWidth > 0 && screenWidth <= NARROW_MOBILE_BREAKPOINT;
  // Shopping channels (DIS-1460): In-Store = Salesforce Live; Online =
  // static ecommerce pickup_available — NOT suburb-scoped delivery_available.
  const hasInStore = hasInStoreGiftCardChannel(item);
  const hasOnline = hasOnlineGiftCardChannel(item);
  // Fulfillment chips: same flags HomePage sets on select
  // (setCanPickup/setCanDeliver from the /members/search row).
  const canPickup = hasGiftCardPickupCapability(item);
  const canDeliver = hasGiftCardDeliveryCapability(item);

  // Redemption-channel labels: one per live channel. A store live in both
  // channels shows both labels (DIS-1459); a store live in neither shows none.
  const pills: PillVariant[] = [];
  if (hasInStore) {
    pills.push('instore');
  }
  if (hasOnline) {
    pills.push('online');
  }

  const openTodayText = formatOpenTodayText(item);

  const distanceBlock = (
    <View style={styles.distanceBlock}>
      <Text style={styles.distanceValue}>{item.distance?.toFixed(1)}</Text>
      <Text style={styles.distanceUnit}>KM</Text>
    </View>
  );

  const capabilityChips = (
    <GiftCardStoreCapabilityChips canPickup={canPickup} canDeliver={canDeliver} />
  );

  // ---- Mobile layout ----
  // Wide-mobile: body holds name+distance, address, hours, pills+chips
  //   - All content left-anchored to the card's left padding.
  // Narrow-mobile (screenWidth <= NARROW_MOBILE_BREAKPOINT):
  //   - The body column gets too tight to hold the pills row without
  //     clipping. The bottom section escapes the body column and spans
  //     the FULL card width so pills get all available horizontal space
  //     to wrap.
  if (!isWebDesktopScreen) {
    const bottomSection = (
      <View style={styles.mobileBottomStack}>
        <View style={styles.mobileBottomRow}>
          <View style={styles.mobilePills}>
            {pills.map(p => (
              <Pill key={p} variant={p} />
            ))}
          </View>
        </View>
        {SHOW_ONLINE_SHOPPING_OPTIONS && (
          <View style={styles.mobileCapabilityChips}>{capabilityChips}</View>
        )}
      </View>
    );

    return (
      <View
        style={[styles.card, styles.cardMobile, isNarrowMobile && styles.cardMobileNarrow, style]}
        testID={testID ?? 'gift-card-store-card'}>
        <View style={styles.body}>
          <View style={styles.mobileNameRow}>
            <Text style={[styles.storeName, styles.storeNameMobile]} numberOfLines={2}>
              {item.name}
            </Text>
            <View style={styles.distanceBlockMobile}>
              <Text style={styles.distanceValueMobile}>{item.distance?.toFixed(1)}</Text>
              <Text style={styles.distanceUnit}>KM</Text>
            </View>
          </View>
          <Text style={styles.address} numberOfLines={1}>
            {renderAddressLines(item?.address?.lines)}
          </Text>
          <Text style={styles.address} numberOfLines={1}>
            {item?.address?.locality}, {item?.address?.administrative_area?.toUpperCase()}{' '}
            {item?.address?.postal_code}
          </Text>
          {!!openTodayText && (
            <Text style={styles.openHours} numberOfLines={1} testID="gift-card-store-open-hours">
              {openTodayText}
            </Text>
          )}
          {/* Wide-mobile: bottom section nests inside body so it left-aligns
              with the store name. */}
          {!isNarrowMobile && bottomSection}
        </View>
        {/* Narrow-mobile: bottom section spans the full card width below the
            body, giving pills room to wrap without being squeezed. */}
        {isNarrowMobile ? bottomSection : null}
      </View>
    );
  }

  // ---- Desktop layout: body | distance | capability chips ----
  return (
    <View style={[styles.card, style]} testID={testID ?? 'gift-card-store-card'}>
      <View style={styles.body}>
        <Text style={styles.storeName} numberOfLines={2}>
          {item.name}
        </Text>
        <Text style={styles.address} numberOfLines={1}>
          {renderAddressLines(item?.address?.lines)}
        </Text>
        <Text style={styles.address} numberOfLines={1}>
          {item?.address?.locality}, {item?.address?.administrative_area?.toUpperCase()}{' '}
          {item?.address?.postal_code}
        </Text>
        {!!openTodayText && (
          <Text style={styles.openHours} numberOfLines={1} testID="gift-card-store-open-hours">
            {openTodayText}
          </Text>
        )}
        {pills.length > 0 && (
          <View style={styles.pillsRow}>
            {pills.map(p => (
              <Pill key={p} variant={p} />
            ))}
          </View>
        )}
      </View>

      <View style={styles.distanceColumn}>{distanceBlock}</View>
      {SHOW_ONLINE_SHOPPING_OPTIONS && (
        <View style={styles.capabilityColumn}>{capabilityChips}</View>
      )}
    </View>
  );
};

export default GiftCardStoreCard;

const styles = StyleSheet.create({
  card: {
    flexDirection: 'row',
    // Top-anchor the row so columns line up consistently across cards.
    // Distance + capability columns opt back into vertical centring via
    // alignSelf so they still share the body's vertical midline.
    alignItems: 'flex-start',
    paddingVertical: 16,
    paddingHorizontal: 16,
    backgroundColor: '#FFFFFF',
    gap: 16,
  },
  // Mobile flips the card to a column so the body stacks with an optional
  // narrow-mode bottom section that spans the full card width.
  cardMobile: {
    flexDirection: 'column',
    alignItems: 'stretch',
    gap: 12,
  },
  // No additional rules needed for narrow-mobile beyond what cardMobile
  // already sets — the narrow flag drives JSX placement, not card style.
  cardMobileNarrow: {},
  body: {
    flex: 1,
    minWidth: 0,
  },
  storeName: {
    fontSize: 15,
    fontWeight: '700',
    color: PRIMARY,
    textTransform: 'uppercase',
    letterSpacing: 0.3,
    marginBottom: 4,
  },
  storeNameMobile: {
    flex: 1,
    marginBottom: 0,
  },
  address: {
    fontSize: 12,
    color: '#4B5563',
    lineHeight: 16,
  },
  openHours: {
    fontSize: 12,
    color: PRIMARY,
    fontWeight: '500',
    marginTop: 4,
    lineHeight: 16,
  },
  pillsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 6,
    marginTop: 10,
  },
  pill: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 999,
    borderWidth: 1,
  },
  pillText: {
    fontSize: 11,
    fontWeight: '500',
  },

  // Desktop fixed column wrappers — give distance + capability chips a known
  // width so values line up vertically across rows like a table.
  // alignSelf: 'center' pulls each column to the body's vertical midline
  // while the card itself is top-anchored.
  distanceColumn: {
    width: DISTANCE_COL_WIDTH,
    alignItems: 'flex-start',
    alignSelf: 'center',
  },
  capabilityColumn: {
    width: CAPABILITY_CHIPS_COL_WIDTH,
    alignItems: 'flex-start',
    alignSelf: 'center',
  },
  distanceBlock: {
    alignItems: 'center',
  },
  distanceValue: {
    fontSize: 20,
    fontWeight: '700',
    color: PRIMARY,
    lineHeight: 22,
  },
  distanceUnit: {
    fontSize: 11,
    fontWeight: '600',
    color: PRIMARY,
    marginTop: 2,
  },

  // Mobile layout
  mobileNameRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 8,
    marginBottom: 4,
  },
  // Distance pinned to the top-right corner of the mobile name row.
  // alignSelf + marginLeft:'auto' force right-anchor; alignItems centers
  // the value/unit horizontally within the block. Card paddingHorizontal
  // (16) handles the right-edge spacing — no extra margin needed.
  distanceBlockMobile: {
    alignItems: 'flex-end',
    alignSelf: 'flex-start',
    marginLeft: 'auto',
    minWidth: 48,
  },
  distanceValueMobile: {
    fontSize: 18,
    fontWeight: '700',
    color: PRIMARY,
    lineHeight: 20,
    textAlign: 'right',
  },
  // Mobile section 2 stack: pills row, then capability chips.
  mobileBottomStack: {
    gap: 10,
    marginTop: 10,
  },
  // Pills row uses alignItems: 'flex-end' so pills anchor to the row's
  // baseline regardless of how many lines they wrap to.
  mobileBottomRow: {
    flexDirection: 'row',
    alignItems: 'flex-end',
    justifyContent: 'space-between',
    gap: 12,
  },
  mobilePills: {
    flex: 1,
    flexDirection: 'row',
    flexWrap: 'wrap',
    alignItems: 'flex-start',
    alignContent: 'flex-start',
    gap: 6,
    paddingTop: 2,
  },
  mobileCapabilityChips: {
    alignSelf: 'flex-start',
  },
});
